import { useCallback, useEffect, useMemo, useState } from 'react';
import { createInitialGameState, type GameState } from '../engine/state.js';
import { perform, travel, type Result } from '../engine/reducer.js';
import type { LoggedAction } from '../engine/replay.js';
import { educationLevel, isAlive } from '../domain/stats.js';
import {
  day,
  hour,
  weekday,
  isWeekend,
  isGameOver,
  GAME_LENGTH_DAYS,
} from '../domain/clock.js';
import { scoreBreakdown } from '../domain/score.js';
import {
  LOCATIONS,
  LOCATION_IDS,
  travelHours,
  CORE_WORK_START,
  CORE_WORK_END,
  type LocationId,
} from '../domain/location.js';

type ActionsTab = 'here' | 'travel';
import { JOBS, nextJob, daysRequiredFor, type JobId } from '../domain/job.js';
import { actionsAtLocation, type ActionId } from '../domain/action.js';
import { FULL_WORKDAY_HOURS } from '../engine/reducer.js';
import { t, plural, formatCurrency, getLocale, type TranslationKey } from '../i18n/index.js';
import { translateError } from '../i18n/errors.js';
import { Turnstile } from './Turnstile.js';

// Turnstile site key vem do env. Set:
//   - local dev:  .env.development.local → VITE_TURNSTILE_SITE_KEY=<dev key>
//   - local prod: .env.production.local  → VITE_TURNSTILE_SITE_KEY=<prod key>
//   - CF build:   dashboard → Workers & Pages → naintufaiv → Settings → Build Variables
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
if (!TURNSTILE_SITE_KEY) {
  // eslint-disable-next-line no-console
  console.error('Missing VITE_TURNSTILE_SITE_KEY at build time — submissions will fail.');
}

type Option =
  | {
      readonly kind: 'action';
      readonly id: ActionId;
      readonly targetJob?: JobId;
      readonly label: string;
      readonly duration: number;
      readonly cost?: number; // cêntimos, negativo se gasta
    }
  | {
      readonly kind: 'travel';
      readonly to: LocationId;
      readonly label: string;
      readonly duration: number;
    };

function actionLabel(id: ActionId): string {
  return t(`action.${id}` as TranslationKey);
}

function jobLabel(id: JobId): string {
  return t(`job.${id}` as TranslationKey);
}

function locationLabel(id: LocationId): string {
  return t(`location.${id}` as TranslationKey);
}

function hoursLabel(loc: LocationId): string {
  const l = LOCATIONS[loc];
  if (l.openHour === l.closeHour) return t('hours.always');
  return t('hours.range', { open: l.openHour, close: l.closeHour });
}

function applyRequirementLabel(needed: number, currentJob: JobId | null): string {
  const previous = currentJob !== null ? jobLabel(currentJob).toLowerCase() : '';
  return plural(needed, 'action.applyRequirement.one', 'action.applyRequirement.other', {
    days: needed,
    previous,
  });
}

function buildOptions(state: GameState): readonly Option[] {
  const c = state.character;
  const opts: Option[] = [];

  for (const action of actionsAtLocation(c.location)) {
    const money = action.effects.money;
    const costEntry = money !== undefined && money < 0 ? { cost: money } : {};
    if (action.id === 'candidatar-se') {
      const upcoming = nextJob(c.jobId);
      if (upcoming) {
        const job = JOBS[upcoming];
        const needed = daysRequiredFor(upcoming);
        const requirement = needed === 0 ? '' : applyRequirementLabel(needed, c.jobId);
        opts.push({
          kind: 'action',
          id: 'candidatar-se',
          targetJob: job.id,
          label: t('action.applyLabel', {
            job: jobLabel(job.id),
            wage: formatCurrency(job.hourlyWage),
            requirement,
          }),
          duration: action.duration,
          ...costEntry,
        });
      }
    } else {
      opts.push({
        kind: 'action',
        id: action.id,
        label: actionLabel(action.id),
        duration: action.duration,
        ...costEntry,
      });
    }
  }

  for (const dest of LOCATION_IDS) {
    if (dest === c.location) continue;
    opts.push({
      kind: 'travel',
      to: dest,
      label: `${locationLabel(dest)} (${hoursLabel(dest)})`,
      duration: travelHours(c.location, dest),
    });
  }

  return opts;
}

function applyOption(state: GameState, opt: Option): Result<GameState> {
  if (opt.kind === 'travel') return travel(state, opt.to);
  return perform(state, opt.id, opt.targetJob !== undefined ? { targetJob: opt.targetJob } : undefined);
}

function toLoggedAction(opt: Option): LoggedAction {
  if (opt.kind === 'travel') return { kind: 'travel', to: opt.to };
  if (opt.targetJob !== undefined) {
    return { kind: 'action', id: opt.id, targetJob: opt.targetJob };
  }
  return { kind: 'action', id: opt.id };
}

function statClass(value: number): string {
  return value <= 25 ? 'low' : '';
}

function mapErrorCodeToI18nKey(code: string): TranslationKey | null {
  switch (code) {
    case 'rate_limited':
      return 'leaderboard.error.rateLimited';
    case 'daily_quota_exhausted':
      return 'leaderboard.error.dailyQuota';
    case 'turnstile_failed':
    case 'missing_turnstile_token':
      return 'leaderboard.error.turnstile';
    case 'duplicate_submission':
      return 'leaderboard.error.duplicate';
    case 'replay_failed':
      return 'leaderboard.error.replay';
    case 'game_not_finished':
      return 'leaderboard.error.notFinished';
    case 'invalid_display_name':
      return 'leaderboard.invalidName';
    default:
      return null;
  }
}

type SubmitStatus =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'submitted'; rank: number | null; qualified: boolean; leaderboardSize: number }
  | { kind: 'error'; reason: string };

type LeaderboardState =
  | { kind: 'closed' }
  | { kind: 'loading' }
  | { kind: 'loaded'; scores: ReadonlyArray<{ id: string; display_name: string; score: number }> }
  | { kind: 'error'; reason: string };

type QualificationState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'qualified' }
  | { kind: 'notQualified'; threshold: number; leaderboardSize: number }
  | { kind: 'error' };

const LEADERBOARD_SIZE = 100;

export function App() {
  const [state, setState] = useState(createInitialGameState);
  const [actions, setActions] = useState<readonly LoggedAction[]>([]);
  const [name, setName] = useState('');
  const [feedback, setFeedback] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submit, setSubmit] = useState<SubmitStatus>({ kind: 'idle' });
  const [leaderboard, setLeaderboard] = useState<LeaderboardState>({ kind: 'closed' });
  const [qualification, setQualification] = useState<QualificationState>({ kind: 'idle' });
  const [actionsTab, setActionsTab] = useState<ActionsTab>('here');

  // Quando viajas, volta automaticamente para o painel "aqui" do novo local.
  useEffect(() => {
    setActionsTab('here');
  }, [state.character.location]);

  const resetGame = useCallback(() => {
    setState(createInitialGameState());
    setActions([]);
    setName('');
    setFeedback('');
    setTurnstileToken(null);
    setSubmit({ kind: 'idle' });
    setLeaderboard({ kind: 'closed' });
    setQualification({ kind: 'idle' });
  }, []);

  const doSubmit = useCallback(async () => {
    const trimmed = name.trim();
    if (trimmed.length < 3 || trimmed.length > 24) {
      setSubmit({ kind: 'error', reason: t('leaderboard.invalidName') });
      return;
    }
    if (!turnstileToken) {
      setSubmit({ kind: 'error', reason: t('leaderboard.turnstileMissing') });
      return;
    }
    setSubmit({ kind: 'submitting' });
    try {
      const trimmedFeedback = feedback.trim();
      const r = await fetch('/api/scores', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          display_name: trimmed,
          action_log: actions,
          locale: getLocale(),
          turnstile_token: turnstileToken,
          ...(trimmedFeedback.length > 0 ? { feedback: trimmedFeedback } : {}),
        }),
      });
      const data = (await r.json()) as {
        rank?: number | null;
        qualified?: boolean;
        leaderboardSize?: number;
        error?: string;
        detail?: string;
      };
      if (!r.ok) {
        const code = data.error ?? `http_${r.status}`;
        const friendlyKey = mapErrorCodeToI18nKey(code);
        throw new Error(friendlyKey ? t(friendlyKey) : (data.detail ?? code));
      }
      setSubmit({
        kind: 'submitted',
        rank: data.rank ?? null,
        qualified: data.qualified !== false,
        leaderboardSize: data.leaderboardSize ?? 100,
      });
    } catch (e) {
      setSubmit({ kind: 'error', reason: e instanceof Error ? e.message : String(e) });
    }
  }, [name, feedback, actions, turnstileToken]);

  const renderLeaderboardModal = () => {
    if (leaderboard.kind === 'closed') return null;
    return (
      <div className="leaderboard-modal" onClick={() => setLeaderboard({ kind: 'closed' })}>
        <div className="leaderboard-content" onClick={(e) => e.stopPropagation()}>
          <h2>{t('leaderboard.title')}</h2>
          {leaderboard.kind === 'loading' && <p>{t('leaderboard.loading')}</p>}
          {leaderboard.kind === 'error' && <p className="error">{leaderboard.reason}</p>}
          {leaderboard.kind === 'loaded' && leaderboard.scores.length === 0 && (
            <p className="empty">{t('leaderboard.empty')}</p>
          )}
          {leaderboard.kind === 'loaded' && leaderboard.scores.length > 0 && (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>{t('leaderboard.col.rank')}</th>
                  <th>{t('leaderboard.col.name')}</th>
                  <th>{t('leaderboard.col.score')}</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.scores.map((row, i) => (
                  <tr key={row.id}>
                    <td>{i + 1}</td>
                    <td>{row.display_name}</td>
                    <td>{row.score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button onClick={() => setLeaderboard({ kind: 'closed' })}>
            {t('leaderboard.close')}
          </button>
        </div>
      </div>
    );
  };

  const openLeaderboard = useCallback(async () => {
    setLeaderboard({ kind: 'loading' });
    try {
      const r = await fetch('/api/scores/top?n=10');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = (await r.json()) as { scores: Array<{ id: string; display_name: string; score: number }> };
      setLeaderboard({ kind: 'loaded', scores: data.scores });
    } catch (e) {
      setLeaderboard({ kind: 'error', reason: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  const c = state.character;
  const s = c.stats;
  const d = day(state.clock);
  const h = hour(state.clock);
  const wd = weekday(state.clock);
  const lvl = educationLevel(s.education);
  const job = c.jobId !== null ? JOBS[c.jobId] : undefined;
  const sc = scoreBreakdown(s, job);
  const gameOver = isGameOver(state.clock) || !isAlive(s);

  const options = useMemo(() => {
    if (gameOver) return [] as const;
    return buildOptions(state);
  }, [state, gameOver]);

  const annotated = useMemo(
    () =>
      options.map((opt) => {
        const r = applyOption(state, opt);
        return r.ok
          ? { opt, valid: true as const }
          : { opt, valid: false as const, reason: translateError(r.code, r.params) };
      }),
    [state, options],
  );

  const hereAnnotated = annotated.filter(({ opt }) => opt.kind === 'action');
  const travelAnnotated = annotated.filter(({ opt }) => opt.kind === 'travel');
  const visibleAnnotated = actionsTab === 'here' ? hereAnnotated : travelAnnotated;

  // When game ends, check if score qualifies for top LEADERBOARD_SIZE before showing submit form.
  // Deliberately depend ONLY on `gameOver` — including `qualification.kind` or `sc.total` in deps
  // causes React to cleanup (cancelled=true) before the fetch resolves, leaving us stuck on 'checking'.
  // sc.total is stable after gameOver, so capturing it in the closure is safe.
  useEffect(() => {
    if (!gameOver) return;
    const finalScore = sc.total;
    setQualification({ kind: 'checking' });
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/scores/top?n=${LEADERBOARD_SIZE}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as { scores: Array<{ score: number }> };
        if (cancelled) return;
        const scores = data.scores;
        if (scores.length < LEADERBOARD_SIZE) {
          setQualification({ kind: 'qualified' });
          return;
        }
        const threshold = Math.min(...scores.map((x) => x.score));
        if (finalScore > threshold) {
          setQualification({ kind: 'qualified' });
        } else {
          setQualification({ kind: 'notQualified', threshold, leaderboardSize: LEADERBOARD_SIZE });
        }
      } catch {
        if (cancelled) return;
        setQualification({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameOver]);

  if (gameOver) {
    return (
      <div className="app">
        <div className="endscreen">
          <h1>{!isAlive(s) ? t('gameover.titleDead') : t('gameover.titleEnd')}</h1>
          <p className="reason">
            {!isAlive(s) ? t('gameover.reasonDead') : t('gameover.reasonEnd')}
          </p>
          <div className="score-total">{sc.total.toFixed(1)}</div>
          <div className="breakdown">
            <div className="breakdown-row">
              <span>{t('gameover.breakdown.money')}</span>
              <span>
                {sc.money.toFixed(1)}{' '}
                <small style={{ color: 'var(--muted)' }}>{formatCurrency(s.money)}</small>
              </span>
            </div>
            <div className="breakdown-row">
              <span>{t('gameover.breakdown.education')}</span>
              <span>
                {sc.education.toFixed(1)} <small style={{ color: 'var(--muted)' }}>L{lvl}</small>
              </span>
            </div>
            <div className="breakdown-row">
              <span>{t('gameover.breakdown.happiness')}</span>
              <span>{sc.happiness.toFixed(1)}</span>
            </div>
            <div className="breakdown-row">
              <span>{t('gameover.breakdown.job')}</span>
              <span>
                {sc.job.toFixed(1)}{' '}
                <small style={{ color: 'var(--muted)' }}>{job ? jobLabel(job.id) : '—'}</small>
              </span>
            </div>
          </div>
          {(qualification.kind === 'idle' || qualification.kind === 'checking') &&
            submit.kind === 'idle' && (
              <p className="submit-status">{t('leaderboard.checking')}</p>
            )}
          {qualification.kind === 'notQualified' && submit.kind === 'idle' && (
            <div className="submit-success">
              <p className="submit-rank not-qualified">
                {t('leaderboard.notQualified', { size: qualification.leaderboardSize })}
              </p>
              <button className="secondary" onClick={openLeaderboard}>
                {t('leaderboard.viewAll')}
              </button>
            </div>
          )}
          {(qualification.kind === 'qualified' || qualification.kind === 'error') &&
            submit.kind === 'idle' && (
              <div className="submit-form">
                <p className="submit-prompt">{t('leaderboard.submitPrompt')}</p>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && name.trim().length >= 3 && turnstileToken) {
                      doSubmit();
                    }
                  }}
                  placeholder={t('leaderboard.namePlaceholder')}
                  maxLength={24}
                  autoFocus
                />
                <label className="submit-feedback-label" htmlFor="submit-feedback">
                  {t('leaderboard.feedbackLabel')}{' '}
                  <span className="submit-feedback-optional">
                    ({t('leaderboard.feedbackOptional')})
                  </span>
                </label>
                <textarea
                  id="submit-feedback"
                  className="submit-feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={t('leaderboard.feedbackPlaceholder')}
                  maxLength={500}
                  rows={3}
                />
                {TURNSTILE_SITE_KEY ? (
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onToken={(tk) => setTurnstileToken(tk)}
                    onExpired={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                  />
                ) : (
                  <p className="submit-error">
                    Turnstile not configured (VITE_TURNSTILE_SITE_KEY missing)
                  </p>
                )}
                <div className="submit-buttons">
                  <button
                    onClick={doSubmit}
                    disabled={name.trim().length < 3 || !turnstileToken}
                  >
                    {t('leaderboard.submit')}
                  </button>
                  <button
                    className="secondary"
                    onClick={() =>
                      setSubmit({
                        kind: 'submitted',
                        rank: null,
                        qualified: false,
                        leaderboardSize: LEADERBOARD_SIZE,
                      })
                    }
                  >
                    {t('leaderboard.skip')}
                  </button>
                </div>
              </div>
            )}
          {submit.kind === 'submitting' && (
            <p className="submit-status">{t('leaderboard.submitting')}</p>
          )}
          {submit.kind === 'submitted' && (
            <div className="submit-success">
              {submit.qualified && submit.rank !== null && (
                <p className="submit-rank">{t('leaderboard.yourRank', { rank: submit.rank })}</p>
              )}
              {!submit.qualified && submit.rank === null && (
                <p className="submit-rank not-qualified">
                  {t('leaderboard.notQualified', { size: submit.leaderboardSize })}
                </p>
              )}
              <button className="secondary" onClick={openLeaderboard}>
                {t('leaderboard.viewAll')}
              </button>
            </div>
          )}
          {submit.kind === 'error' && (
            <div className="submit-error">
              <p>{t('leaderboard.submitFailed', { reason: submit.reason })}</p>
              <button onClick={() => setSubmit({ kind: 'idle' })}>
                {t('leaderboard.submit')}
              </button>
            </div>
          )}
          <button onClick={resetGame}>{t('gameover.newGame')}</button>
        </div>
        {renderLeaderboardModal()}
      </div>
    );
  }

  return (
    <div className="app">
      <div className="top">
      <div className="hud">
        <div>
          <div className="hud-day">{t('hud.day')}</div>
          <div className="hud-time">
            {d + 1}/{GAME_LENGTH_DAYS}
          </div>
        </div>
        <div>
          <div className="hud-day">{t(`weekday.${wd}` as TranslationKey)}</div>
          <div className="hud-time">{h.toString().padStart(2, '0')}h</div>
        </div>
        <div className="hud-location">
          <div className="hud-day">{t('hud.location', { hours: hoursLabel(c.location) })}</div>
          <div>{LOCATIONS[c.location].emoji} {locationLabel(c.location)}</div>
        </div>
        <div className="hud-score">
          <div className="hud-score-label">{t('hud.score')}</div>
          <div className="hud-score-value">{sc.total.toFixed(0)}</div>
        </div>
        <button className="hud-leaderboard" onClick={openLeaderboard} title={t('leaderboard.title')}>
          🏆
        </button>
      </div>

      <div className="stats">
        <div className="stat-row">
          <span className="stat-name">{t('stat.energy')}</span>
          <div className="stat-bar">
            <div
              className={`stat-bar-fill energy ${statClass(s.energy)}`}
              style={{ width: `${s.energy}%` }}
            />
          </div>
          <span className="stat-value">{s.energy.toFixed(0)}</span>
        </div>
        <div className="stat-row">
          <span className="stat-name">{t('stat.happiness')}</span>
          <div className="stat-bar">
            <div
              className={`stat-bar-fill happiness ${statClass(s.happiness)}`}
              style={{ width: `${s.happiness}%` }}
            />
          </div>
          <span className="stat-value">{s.happiness.toFixed(0)}</span>
        </div>
        <div className="stat-row">
          <span className="stat-name">{t('stat.hunger')}</span>
          <div className="stat-bar">
            <div
              className={`stat-bar-fill hunger ${statClass(s.hunger)}`}
              style={{ width: `${s.hunger}%` }}
            />
          </div>
          <span className="stat-value">{s.hunger.toFixed(0)}</span>
        </div>
      </div>

      <div className="meta">
        <span>
          {t('meta.job')}: <strong>{job ? jobLabel(job.id) : '—'}</strong>
          {(() => {
            const upcoming = nextJob(c.jobId);
            if (!upcoming) return null;
            const needed = daysRequiredFor(upcoming);
            if (needed === 0) return null;
            return (
              <small style={{ color: 'var(--muted)' }}>
                {' '}
                {t('meta.daysForPromotion', {
                  current: c.fullWorkdaysInRole,
                  needed,
                  target: jobLabel(upcoming),
                })}
              </small>
            );
          })()}
        </span>
        <span>
          {t('meta.education', {
            xp: s.education,
            level: lvl,
            levelName: t(`education.${lvl}` as TranslationKey),
          })}
        </span>
        <span>
          {plural(s.foodAtHome, 'meta.fridge.one', 'meta.fridge.other')}
        </span>
        <span className="meta-money">{formatCurrency(s.money)}</span>
        {c.hasSuit && <span className="meta-suit">{t('meta.hasSuit')}</span>}
      </div>

      {(() => {
        const hasJob = c.jobId !== null;
        const weekend = isWeekend(state.clock);
        if (!hasJob || weekend) return null;
        const coreEnded = h >= CORE_WORK_END;
        const done = c.hoursWorkedToday >= FULL_WORKDAY_HOURS;
        const warned = c.warnings >= 1;
        const tplParams = {
          start: CORE_WORK_START,
          end: CORE_WORK_END,
          hours: c.hoursWorkedToday,
          max: FULL_WORKDAY_HOURS,
        };
        if (done) {
          return (
            <div className="work-warning ok">
              {t('work.done', tplParams)}
              {warned ? t('work.willClearWarning') : ''}
            </div>
          );
        }
        if (coreEnded) {
          return (
            <div className="work-warning urgent">
              {warned ? t('work.coreEndedDemote', tplParams) : t('work.coreEndedWarn', tplParams)}
            </div>
          );
        }
        return (
          <div className={`work-warning ${warned ? 'urgent' : ''}`}>
            {warned ? t('work.warningPrefix') : ''}
            {t('work.progress', { ...tplParams, openLabel: hoursLabel('trabalho') })}
          </div>
        );
      })()}

      <nav className="tabs" role="tablist" aria-label="Acções">
        <button
          type="button"
          role="tab"
          aria-selected={actionsTab === 'here'}
          className="tab"
          onClick={() => setActionsTab('here')}
        >
          {locationLabel(c.location)}
          <span className="tab-count"> ({hereAnnotated.length})</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={actionsTab === 'travel'}
          className="tab"
          onClick={() => setActionsTab('travel')}
        >
          {t('tab.travel')}
          <span className="tab-count"> ({travelAnnotated.length})</span>
        </button>
      </nav>
      </div>

      <div className="scroll">
      <div className="actions">
        {visibleAnnotated.map(({ opt, valid, ...rest }, i) => {
          const reason = 'reason' in rest ? rest.reason : undefined;
          const isTravel = opt.kind === 'travel';
          return (
            <button
              key={i}
              className={`action ${isTravel ? 'travel' : ''}`}
              disabled={!valid}
              onClick={() => {
                if (!valid) return;
                const r = applyOption(state, opt);
                if (r.ok) {
                  setState(r.value);
                  setActions((prev) => [...prev, toLoggedAction(opt)]);
                }
              }}
            >
              <span>
                <span className="action-label">{opt.label}</span>
                {!valid && reason && <span className="action-reason">{reason}</span>}
              </span>
              <span className="action-meta">
                {opt.kind === 'action' && opt.cost !== undefined && (
                  <span className="action-cost">{formatCurrency(opt.cost)}</span>
                )}
                <span className="action-duration">{opt.duration}h</span>
              </span>
            </button>
          );
        })}
      </div>
      </div>
      {renderLeaderboardModal()}
    </div>
  );
}
